import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import {
  type ESocialNetwork,
  type ICountry,
  type ISocialPostSuggestion,
} from '@mahawi/eshop-common/src/types';
import { Badge, Button, Input, Modal, Space, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import React, { type Key, useCallback, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { EMPTY_VALUE } from '../../fragments/empty-value';
import { SPACING } from '../../theme/tokens';
import Link from '../link';
import TableComponent from '../table';

const { Text } = Typography;
const { TextArea } = Input;

const STATUS_COLOR: Record<string, string> = {
  draft: 'blue',
  published: 'green',
  skipped: 'default',
};

function HistoryTable({
  history,
  countries,
  loading,
  onPublish,
  onSkip,
}: {
  history: ISocialPostSuggestion[];
  countries: ICountry[];
  loading: boolean;
  onPublish: (
    id: number,
    text: string,
    socialNetwork: ESocialNetwork,
    productUUID: string,
    country: string,
  ) => void;
  onSkip: (id: number) => void;
}): React.ReactElement {
  const countryLabel = (isoCode: string): string => {
    const country: ICountry | undefined = countries.find(
      (c: ICountry): boolean => c.isoCode === isoCode || c.isoCode2 === isoCode,
    );
    const name: string = country?.name ?? isoCode;
    const code: string = country?.isoCode ?? isoCode;
    return `${getFlag({ countryTypeIsoCode: isoCode })} ${name} (${code})`;
  };
  const [editingRow, setEditingRow] = useState<ISocialPostSuggestion | null>(
    null,
  );
  const [editedText, setEditedText] = useState<string>('');

  const openEdit = useCallback((row: ISocialPostSuggestion): void => {
    setEditingRow(row);
    setEditedText(row.suggestedText);
  }, []);

  const handleModalPublish = useCallback((): void => {
    if (!editingRow) {
      return;
    }
    onPublish(
      editingRow.id,
      editedText,
      editingRow.socialNetwork as ESocialNetwork,
      editingRow.productUUID,
      editingRow.country,
    );
    setEditingRow(null);
  }, [editingRow, editedText, onPublish]);

  const handleModalCancel = useCallback((): void => {
    setEditingRow(null);
  }, []);

  const handleSkip = useCallback(
    (id: number): void => {
      onSkip(id);
    },
    [onSkip],
  );

  const columns: ColumnsType<ISocialPostSuggestion> = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (value: string): string => value.slice(0, 10),
      sorter: (a: ISocialPostSuggestion, b: ISocialPostSuggestion): number =>
        a.createdAt.localeCompare(b.createdAt),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      width: 200,
      render: (value: string): string => countryLabel(value),
      filters: Array.from(
        new Set(history.map((r: ISocialPostSuggestion): string => r.country)),
      ).map((c: string): { text: string; value: string } => ({
        text: countryLabel(c),
        value: c,
      })),
      onFilter: (
        value: boolean | Key,
        record: ISocialPostSuggestion,
      ): boolean => record.country === value,
    },
    {
      title: 'Product',
      dataIndex: 'productUUID',
      key: 'productUUID',
      width: 120,
      render: (value: string): React.ReactElement => (
        <Link
          to={`/product/${value}`}
          label={value.slice(0, 8) + '…'}
          openInNewTab
        />
      ),
    },
    {
      title: 'Text',
      dataIndex: 'suggestedText',
      key: 'suggestedText',
      render: (value: string): React.ReactElement => (
        <Text ellipsis={{ tooltip: value }}>
          {value.length > 80 ? value.slice(0, 80) + '…' : value}
        </Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (value: string): React.ReactElement => (
        <Badge color={STATUS_COLOR[value] ?? 'default'} text={value} />
      ),
      filters: [
        { text: 'draft', value: 'draft' },
        { text: 'published', value: 'published' },
        { text: 'skipped', value: 'skipped' },
      ],
      onFilter: (
        value: boolean | Key,
        record: ISocialPostSuggestion,
      ): boolean => record.status === value,
    },
    {
      title: 'Short URL',
      dataIndex: 'shortUrl',
      key: 'shortUrl',
      width: 120,
      render: (value: string | null): React.ReactElement | string => {
        if (!value) {
          return EMPTY_VALUE;
        }
        return (
          <RouterLink
            to={value}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {value.split('/s/')[1] ?? value}
          </RouterLink>
        );
      },
    },
    {
      title: 'External Post ID',
      dataIndex: 'externalPostId',
      key: 'externalPostId',
      width: 140,
      render: (value: string | null): string => value ?? EMPTY_VALUE,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (
        _: ISocialPostSuggestion,
        record: ISocialPostSuggestion,
      ): React.ReactElement => {
        if (record.status !== 'draft') {
          return <Text type="secondary">{EMPTY_VALUE}</Text>;
        }
        return (
          <Space size={SPACING.xs}>
            <Button size="small" onClick={(): void => openEdit(record)}>
              Edit & Publish
            </Button>
            <Button
              size="small"
              danger
              onClick={(): void => handleSkip(record.id)}
            >
              Skip
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <TableComponent
        dataSource={history}
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        rowKey="id"
        defaultPageSize={30}
        size="small"
        isLoading={loading}
      />

      <Modal
        title="Edit & Publish"
        open={!!editingRow}
        onOk={handleModalPublish}
        onCancel={handleModalCancel}
        okText="Publish"
        okButtonProps={{ disabled: !editedText.trim() }}
        width={640}
      >
        <TextArea
          rows={8}
          value={editedText}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>): void =>
            setEditedText(e.target.value)
          }
        />
      </Modal>
    </>
  );
}

export default HistoryTable;
